import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Pipette, X } from 'lucide-react'

interface RgbaColor {
  r: number
  g: number
  b: number
  a: number
}

interface EyeDropperResult {
  sRGBHex: string
}

interface EyeDropperInstance {
  open: () => Promise<EyeDropperResult>
}

type EyeDropperConstructor = new () => EyeDropperInstance

interface ColorWellProps {
  value: string
  onChange: (value: string) => void
  ariaLabel: string
}

const QUICK_COLORS = [
  '#000000', '#262626', '#555555', '#888888', '#cccccc', '#ffffff',
  '#791f1f', '#a32d2d', '#e24b4a', '#f09595', '#fcebeb',
  '#633806', '#854f0b', '#ba7517', '#ef9f27', '#faeeda',
  '#085041', '#0f6e56', '#1d9a6c', '#5dcaa5', '#e1f5ee',
  '#0c447c', '#185fa5', '#378add', '#85b7eb', '#e6f1fb',
]

const FALLBACK_COLOR: RgbaColor = { r: 255, g: 255, b: 255, a: 1 }

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function parseHex(value: string): RgbaColor | null {
  const hex = value.replace('#', '').trim()
  if (![3, 4, 6, 8].includes(hex.length) || !/^[0-9a-f]+$/i.test(hex)) return null
  const expanded = hex.length <= 4 ? hex.split('').map(char => char + char).join('') : hex
  const hasAlpha = expanded.length === 8
  return {
    r: Number.parseInt(expanded.slice(0, 2), 16),
    g: Number.parseInt(expanded.slice(2, 4), 16),
    b: Number.parseInt(expanded.slice(4, 6), 16),
    a: hasAlpha ? Number.parseInt(expanded.slice(6, 8), 16) / 255 : 1,
  }
}

function parseRgb(value: string): RgbaColor | null {
  const match = value.trim().match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/i)
  if (!match) return null
  return {
    r: clamp(Math.round(Number(match[1])), 0, 255),
    g: clamp(Math.round(Number(match[2])), 0, 255),
    b: clamp(Math.round(Number(match[3])), 0, 255),
    a: clamp(match[4] === undefined ? 1 : Number(match[4]), 0, 1),
  }
}

function parseColor(value: string): RgbaColor | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  if (trimmed.toLowerCase() === 'transparent') return { r: 0, g: 0, b: 0, a: 0 }
  const literal = trimmed.startsWith('#') ? parseHex(trimmed) : parseRgb(trimmed)
  if (literal) return literal
  if (typeof document === 'undefined' || !CSS.supports('color', trimmed)) return null
  const context = document.createElement('canvas').getContext('2d')
  if (!context) return null
  context.fillStyle = trimmed
  const normalized = context.fillStyle
  return normalized.startsWith('#') ? parseHex(normalized) : parseRgb(normalized)
}

function toHex(color: RgbaColor): string {
  return `#${[color.r, color.g, color.b].map(channel => clamp(Math.round(channel), 0, 255).toString(16).padStart(2, '0')).join('')}`
}

function toCss(color: RgbaColor): string {
  if (color.a >= .999) return toHex(color)
  const alpha = Number(clamp(color.a, 0, 1).toFixed(3))
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`
}

export const ColorWell = memo(function ColorWell({ value, onChange, ariaLabel }: ColorWellProps) {
  const triggerRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const nativeInputRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(value)
  const [error, setError] = useState('')
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const color = useMemo(() => parseColor(value) ?? FALLBACK_COLOR, [value])
  const nativeEyeDropper = typeof window !== 'undefined'
    ? (window as Window & { EyeDropper?: EyeDropperConstructor }).EyeDropper
    : undefined

  useEffect(() => { setDraft(value); setError('') }, [value])

  const reposition = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const width = 286
    const height = popoverRef.current?.offsetHeight ?? 330
    const left = clamp(rect.left, 8, Math.max(8, window.innerWidth - width - 8))
    const below = rect.bottom + 6
    const top = below + height <= window.innerHeight - 8
      ? below
      : Math.max(8, rect.top - height - 6)
    setPosition({ top, left })
  }, [])

  useLayoutEffect(() => {
    if (!open) return
    reposition()
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (!triggerRef.current?.contains(target) && !popoverRef.current?.contains(target)) {
        // Commit before unmounting the input. A pointerdown outside can remove
        // the popover before blur (notably when clicking Enregistrer).
        if (draft !== value) {
          const parsed = parseColor(draft)
          if (parsed) { onChange(toCss(parsed)); setError('') }
          else setError('Couleur invalide. Utilisez #RRGGBB, rgba(…) ou une couleur CSS.')
        }
        setOpen(false)
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [open, reposition, draft, value, onChange])

  const applyColor = useCallback((next: RgbaColor) => {
    const css = toCss(next)
    setDraft(css)
    setError('')
    onChange(css)
  }, [onChange])

  const applyRgb = useCallback((nextValue: string) => {
    const next = parseColor(nextValue)
    if (next) applyColor({ ...next, a: color.a === 0 ? 1 : color.a })
  }, [applyColor, color.a])

  const commitDraft = useCallback(() => {
    const parsed = parseColor(draft)
    if (parsed) applyColor(parsed)
    else setError('Couleur invalide. Utilisez #RRGGBB, rgba(…) ou une couleur CSS.')
  }, [applyColor, draft])

  const sampleScreen = useCallback(async () => {
    if (!nativeEyeDropper) return
    try {
      const result = await new nativeEyeDropper().open()
      applyRgb(result.sRGBHex)
    } catch {
      // The browser rejects the promise when the operator cancels the sampler.
    }
  }, [applyRgb, nativeEyeDropper])

  const swatchStyle = { '--cg-current-color': toCss(color) } as CSSProperties
  const alphaStyle = { '--cg-alpha-color': toHex(color) } as CSSProperties

  return (
    <>
      <div ref={triggerRef} className="cg-color-well" style={swatchStyle}>
        <button
          type="button"
          className="cg-color-well-swatch cg-transparency-grid"
          onClick={() => setOpen(current => !current)}
          aria-label={ariaLabel}
          aria-invalid={Boolean(error)}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <span aria-hidden="true" />
        </button>
        <button
          type="button"
          className="cg-color-well-caret"
          onClick={() => nativeInputRef.current?.click()}
          title="Ouvrir le sélecteur de couleur natif"
          aria-label="Ouvrir le sélecteur de couleur natif"
        >
          <ChevronDown size={13} />
        </button>
        <input
          ref={nativeInputRef}
          className="cg-native-color-input"
          type="color"
          value={toHex(color)}
          tabIndex={-1}
          aria-hidden="true"
          onChange={event => applyRgb(event.target.value)}
        />
      </div>

      {error && <small role="alert" className="cg-settings-error">{error}</small>}
      {open && createPortal(
        <div
          ref={popoverRef}
          className="cg-color-popover"
          style={{ top: position.top, left: position.left }}
          role="dialog"
          aria-label={ariaLabel}
          aria-invalid={Boolean(error)}
        >
          <header>
            <strong>Couleur</strong>
            <button type="button" className="cg-color-popover-close" onClick={() => setOpen(false)} title="Fermer" aria-label="Fermer"><X size={14} /></button>
          </header>

          <div className="cg-color-palette" aria-label="Palette rapide">
            <button
              type="button"
              className={`cg-color-chip cg-transparency-grid${color.a === 0 ? ' is-selected' : ''}`}
              onClick={() => applyColor({ ...color, a: 0 })}
              title="Transparent"
              aria-label="Transparent"
            ><span /></button>
            {QUICK_COLORS.map(option => {
              const optionColor = parseHex(option) ?? FALLBACK_COLOR
              const selected = color.a > 0 && optionColor.r === color.r && optionColor.g === color.g && optionColor.b === color.b
              return (
                <button
                  type="button"
                  key={option}
                  className={`cg-color-chip${selected ? ' is-selected' : ''}`}
                  style={{ '--cg-chip-color': option } as CSSProperties}
                  onClick={() => applyRgb(option)}
                  title={option}
                  aria-label={option}
                />
              )
            })}
          </div>

          <div className="cg-color-popover-actions">
            <button type="button" className="cg-button" onClick={sampleScreen} disabled={!nativeEyeDropper} title={nativeEyeDropper ? 'Prélever une couleur à l’écran' : 'Pipette indisponible dans ce navigateur'}>
              <Pipette size={14} />Pipette
            </button>
            <button type="button" className="cg-button" onClick={() => nativeInputRef.current?.click()}>
              Sélecteur complet
            </button>
          </div>

          <label className="cg-color-value">
            <span>Valeur CSS</span>
            <input
              value={draft}
              aria-invalid={Boolean(error)}
              onChange={event => setDraft(event.target.value)}
              onBlur={commitDraft}
              onKeyDown={event => { if (event.key === 'Enter') commitDraft() }}
              spellCheck={false}
            />
          </label>

          {error && <small role="alert" className="cg-settings-error">{error}</small>}
          <label className="cg-alpha-control" style={alphaStyle}>
            <span>Opacité</span>
            <input
              type="range"
              min="0"
              max="100"
              value={Math.round(color.a * 100)}
              onChange={event => applyColor({ ...color, a: Number(event.target.value) / 100 })}
            />
            <span className="cg-alpha-value">
              <input
                type="number"
                min="0"
                max="100"
                value={Math.round(color.a * 100)}
                onChange={event => applyColor({ ...color, a: clamp(Number(event.target.value), 0, 100) / 100 })}
              />
              %
            </span>
          </label>
        </div>,
        document.body,
      )}
    </>
  )
})
